import { intersection, isEmpty } from "lodash";

type FeatureFlagResolver = (flagMap: { [key: string]: boolean }) => boolean;

const operatorTypeMap = {
  csv: ["csv"],
  multiSelect: ["in", "notIn"],
  number: ["=", ">", ">=", "<", "<="],
  date: ["on", ">", ">=", "<", "<="],
  singleSelect: ["="],
  string: ["contains", "excludes"],
};

interface SearchableFieldBase {
  contextList: string[];
  dataType: string;
  name: string;
  operations: string;
  resolveFeatureFlag?: FeatureFlagResolver;
  value: string;
  valueList: string | null;
}
interface SearchableField extends Omit<SearchableFieldBase, "operations"> {
  operations: string[];
}

interface SearchableFieldState {
  list: SearchableField[];
}

const listBase: SearchableFieldBase[] = [
  {
    name: "Assigned To",
    contextList: ["memberFields"],
    dataType: "user",
    operations: "multiSelect",
    value: "assignedTo",
    valueList: null,
  },
  {
    name: "Background",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "background",
    valueList: null,
  },
  {
    name: "Build Analyst",
    contextList: ["ochinFields"],
    dataType: "user",
    operations: "multiSelect",
    value: "buildAnalyst",
    valueList: null,
  },
  {
    name: "Build JIRA",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "buildJira",
    valueList: null,
  },
  {
    name: "Build Type",
    contextList: ["autoAssignment", "commonFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "buildType",
    valueList: "buildType",
  },
  {
    name: "Care Setting",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "careSetting",
    valueList: "careSetting",
  },
  {
    name: "Comments",
    contextList: ["memberFields"],
    dataType: "string",
    operations: "string",
    value: "additionalComments",
    valueList: null,
  },
  {
    name: "Current Behavior",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "currentBehavior",
    valueList: null,
  },
  {
    name: "Date Published",
    contextList: ["commonFields"],
    dataType: "dateString",
    operations: "date",
    value: "datePublished",
    valueList: null,
  },
  {
    name: "Documentation Status",
    contextList: ["ochinFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "documentationStatus",
    valueList: "documentationStatus",
  },
  {
    name: "Documented on Behalf of",
    contextList: ["ochinFields"],
    dataType: "user",
    operations: "singleSelect",
    value: "documentedOnBehalfOf",
    valueList: null,
  },
  {
    name: "Application",
    contextList: ["autoAssignment", "commonFields"],
    dataType: "dynamicDropdown",
    operations: "multiSelect",
    value: "applicationDropdownValueId",
    valueList: null,
  },
  {
    name: "Governed Template",
    contextList: ["ochinFields"],
    dataType: "integerBoolean",
    operations: "singleSelect",
    value: "isGovernedTemplate",
    valueList: null,
  },
  {
    name: "Impacts (Service Area Attributes)",
    contextList: ["commonFields"],
    dataType: "serviceAreaAttribute",
    operations: "multiSelect",
    value: "serviceAreaAttributeList",
    valueList: null,
  },
  {
    name: "Implementation Status",
    contextList: ["memberFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "implementationStatus",
    valueList: "implementationStatus",
  },
  {
    name: "Issue",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "issue",
    valueList: null,
  },
  {
    name: "New Behavior",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "newBehavior",
    valueList: null,
  },
  {
    name: "Note",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "note",
    valueList: null,
  },
  {
    name: "Operationalize Type",
    contextList: ["autoAssignment", "commonFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "operationalizeType",
    valueList: "operationalizeType",
  },
  {
    name: "Optional Enhancement",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "optionalEnhancement",
    valueList: null,
  },
  {
    name: "Other Activities Impacted",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "activitiesImpacted",
    valueList: null,
  },
  {
    name: "Other User Roles",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "otherUserRoles",
    valueList: null,
  },
  {
    name: "Packs",
    contextList: ["globalSearchFields"],
    dataType: "pack",
    operations: "multiSelect",
    value: "packId",
    valueList: null,
  },
  {
    name: "Pack Status",
    contextList: ["globalSearchFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "packStatus",
    valueList: "packStatus",
  },
  {
    name: "Pack Type",
    contextList: ["globalSearchFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "packType",
    valueList: "packType",
  },
  {
    name: "Pack Creation Date",
    contextList: ["globalSearchFields"],
    dataType: "dateString",
    operations: "date",
    value: "packCreationDate",
    valueList: null,
  },
  {
    name: "Go-Live Setup",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "preGoLiveSetup",
    valueList: null,
  },
  {
    name: "Primary Activity",
    contextList: ["autoAssignment", "commonFields"],
    dataType: "dynamicDropdown",
    operations: "multiSelect",
    value: "primaryActivityDropdownValueId",
    valueList: null,
  },
  {
    name: "QA Analyst",
    contextList: ["ochinFields"],
    dataType: "user",
    operations: "multiSelect",
    value: "qaAnalyst",
    valueList: null,
  },
  {
    name: "QA JIRA",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "qaJira",
    valueList: null,
  },
  {
    name: "Release Note ID",
    contextList: ["memberFields"],
    dataType: "number",
    operations: "csv",
    value: "releaseNoteId",
    valueList: null,
  },
  {
    name: "Release Note ID",
    contextList: ["ochinFields"],
    dataType: "number",
    operations: "csv",
    value: "id",
    valueList: null,
  },
  {
    name: "Release Type",
    contextList: ["autoAssignment", "commonFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "releaseType",
    valueList: "releaseType",
  },
  {
    name: "JIRA Support Analyst",
    contextList: ["ochinFields"],
    dataType: "user",
    operations: "multiSelect",
    value: "jiraSupportAnalyst",
    valueList: null,
  },
  {
    name: "Reported",
    contextList: ["memberFields"],
    dataType: "integerBoolean",
    operations: "singleSelect",
    value: "reported",
    valueList: null,
  },
  {
    name: "Reported Comments",
    contextList: ["memberFields"],
    dataType: "string",
    operations: "string",
    value: "reportedComments",
    valueList: null,
  },
  {
    name: "Reported Date",
    contextList: ["memberFields"],
    dataType: "dateString",
    operations: "date",
    value: "reportedDate",
    valueList: null,
  },
  {
    name: "Summary",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "summary",
    valueList: null,
  },
  {
    name: "Template Type",
    contextList: ["ochinFields"],
    dataType: "templateType",
    operations: "multiSelect",
    value: "templateType",
    valueList: "templateType",
  },
  {
    name: "Testing/Review Status",
    contextList: ["memberFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "memberTestPlan.status",
    valueList: "testingStatus",
  },
  {
    name: "Title",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "title",
    valueList: null,
  },
  {
    name: "Training JIRA",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "trainingJira",
    valueList: null,
  },
  {
    name: "UAT JIRA",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "uatJira",
    valueList: null,
  },
  {
    name: "Update",
    contextList: ["commonFields"],
    dataType: "string",
    operations: "string",
    value: "updatedReason",
    valueList: null,
  },
  {
    name: "User Role",
    contextList: ["autoAssignment", "commonFields"],
    dataType: "string",
    operations: "multiSelect",
    value: "userRole",
    valueList: "userRoles",
  },
];

const getOperatorList = (operatorCategory: string): string[] => {
  return operatorTypeMap[operatorCategory];
};

const list: SearchableField[] = listBase.map(searchableField => {
  const { name, contextList, dataType, value, valueList, resolveFeatureFlag } = searchableField;

  const newObj: SearchableField = {
    name,
    contextList,
    dataType,
    operations: getOperatorList(searchableField.operations),
    value,
    valueList,
  };
  if (!!resolveFeatureFlag) {
    newObj.resolveFeatureFlag = resolveFeatureFlag;
  }

  return newObj;
});

const state = {
  list,
};

const getters = {
  getAggregateListByContexts: (state: SearchableFieldState, getters: any, rootState: any) => (
    contextList: string[]
  ) => {
    if (!state.list) {
      return [];
    }
    const flagMap = rootState.featureFlag.flag;
    const aggregateList = state.list.filter(
      (field: SearchableField) => !isEmpty(intersection(contextList, field.contextList))
    );
    return resolveFeatureFlags(aggregateList, flagMap);
  },
};

export const resolveFeatureFlags = function(fieldList: SearchableField[], flagMap) {
  return fieldList.filter(field => {
    const { resolveFeatureFlag } = field;

    if (resolveFeatureFlag) {
      return resolveFeatureFlag(flagMap);
    }
    return true;
  });
};

export default {
  getters,
  namespaced: true,
  state,
};
